import React, {useState, useEffect} from 'react';
import {
  ScrollView,
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  Keyboard,
} from 'react-native';
import moment from 'moment';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {navigationRef, currentKey} from '../Shared/RootNavigation';
import {DynamicInputProps, DynamicSelectItem} from '../../Types/ControlTypes';
import DynamicSelect from './DynamicSelect';
import DynamicAutocomplete from './DynamicAutocomplete';
import CustomPressable from '../Shared/CustomPressable';
import Icon from '../Shared/Icon';
import DynamicPDF from '../DynamicForm/DynamicPDF';
import {ObjectId} from 'bson';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {
  SubmissionBundle_submissions,
  TemplateVersion,
  EquipmentVehicleProfile,
  EquipmentHeavyProfile,
  EquipmentOtherProfile,
} from '../../Models/PowerSyncModels/models';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {Navigation} from '../../Constants/Navigation';
import {Icons} from '../../Constants/Icons';
import Colors from '../../Styles/Shared/Colors';
import {Formats} from '../../Constants/Formats';
import {
  METADATA_KEYS,
  COMMON_TEMPLATES,
  LOCK_STATUS,
  CATEGORY,
} from '../../Constants/AppConstants';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {EquipmentOtherFieldName, MongoIdSqlId} from '../../Types/DtoTypes';
import {
  TemplateCategoryTypes,
  TemplateTypes,
} from '../../Constants/TemplateCategoryTypes';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';
import GeneralHelperService from '../../Services/GeneralHelperService';

export declare type DynamicBundleLinkRecordProps = {
  navigation: any;
  label?: string;
  bundleId: string;
  config?: {required?: boolean};
  showError?: boolean;
  showAddButton?: boolean;
  setShowModal?: (func: () => void) => void;
} & DynamicInputProps;

type Record = {
  id: string;
  name: string;
  category: string;
  tagline: string;
  type: 'submission' | 'submissionTraining';
  datetime: Date;
  icon: string;
  org?: string;
  project?: string;
  person?: string;
  subTitle?: string;
};

const DynamicBundleLinkRecord = (
  props: DynamicBundleLinkRecordProps,
): React.ReactElement => {
  const [value, setValue] = useState<string[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [error, setError] = useState('');

  const [selectedRecord, setSelectedRecord] = useState<Record>();
  const [isShowingPDFModal, setIsShowingPDFModal] = useState(false);
  const [recordToPDF, setRecordToPDF] = useState<Record>();

  const [isShowingModal, setIsShowingModal] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [records, setRecords] = useState<Record[]>([]);
  const [valueRecords, setValueRecords] = useState<Record[]>([]);

  const [isShowingFilters, setIsShowingFilters] = useState(false);
  const [categories, setCategories] = useState<DynamicSelectItem[]>([]);
  const [filteredCategory, setFilteredCategory] = useState('');
  const [filteredProject, setFilteredProject] = useState('');
  const [filteredPerson, setFilteredPerson] = useState('');

  const defaultItemsToShow = 10;
  const [itemsToShow, setItemsToShow] = useState(defaultItemsToShow);

  const [myKey, setMyKey] = useState('');
  const [navKey, setNavKey] = useState('');

  const {
    templates,
    templateVersions,
    submissions,
    submissionTrainings,
    submissionBundles,
    submissionLinks,
    equipmentHeavyProfiles,
    equipmentOtherProfiles,
    equipmentVehicleProfiles,
  } = usePowerSyncData();

  const {updateSubmission, updateSubmissionBundle, updateSubmissionTraining} =
    usePowerSyncWriteData();

  const user = usePowerSyncUser();

  const helperService: IGeneralHelperService = new GeneralHelperService();
  
  useEffect(() => {
    setMyKey(currentKey());
    setNavKey(currentKey());

    if (navigationRef.current) {
      const unsubscribe = navigationRef.current.addListener('state', () => {
        setNavKey(currentKey());
      });

      return unsubscribe;
    }
  }, []);

  useEffect(() => {
    fetchValueRecords();

    const getValue = async () => {
      let newValue: string[] = [];
      let bundle = submissionBundles.find(
        b => b._id && b._id == props.bundleId,
      );
      if (bundle && bundle.submissions) {
        bundle.submissions.forEach(s => {
          if (s.submissionId) newValue.push(s.submissionId);
        });
      }
      setValue(newValue);
    };

    getValue();
    fetchRecords();
  }, []);

  useEffect(() => {
    if (props.onChange)
      props.onChange(
        props.controlId,
        props.controlTypeId,
        '',
        valid(),
        null,
        null,
      );

    fetchValueRecords();
  }, [value]);

  useEffect(() => {
    if (props.showError) valid();
  }, [props.showError]);

  useEffect(() => {
    if (props.setShowModal)
      props.setShowModal(() => {
        showModal();
      });
  }, []);

  useEffect(() => {
    if (isShowingModal) {
      if (value) {
        setSelection(JSON.parse(JSON.stringify(value)));
      } else setSelection([]);
    }
  }, [isShowingModal]);

  async function fetchRecords(): Promise<void> {
    let orgId = user.getUserInfo().orgId.toString();

    const mappedRecords: Record[] = [];

    // Fetch Submissions
    let filteredTemplates = templates;
    let filteredVersions = templateVersions;
    let vehicles = equipmentVehicleProfiles;
    let heavyEquipments = equipmentHeavyProfiles;
    let otherEquipments = equipmentOtherProfiles;
    let filteredSubmissions = submissions.filter(
      s =>
        s.partition === orgId &&
        s.submissionStatus &&
        ((s.templateType.name != TemplateTypes.TASK &&
          s.submissionStatus.name === SubmissionStatuses.SUBMITTED) ||
          (s.templateType.name == TemplateTypes.TASK &&
            (s.submissionStatus.name === SubmissionStatuses.COMPLETE ||
              s.submissionStatus.name === SubmissionStatuses.DISMISSED))),
    );

    filteredSubmissions.forEach(function (s, i) {
      let version = filteredVersions.find(v => v._id == s.templateVersion._id);
      let template = filteredTemplates.find(t => t._id == version?.templateId);

      if (template) {
        let title = template.name;
        let subtitle = '';
        let category = template.templateCategory.templateCategoryName;
        let tagline = '';

        if (category.toUpperCase() === CATEGORY.INVESTIGATION) {
          let submissionLink = submissionLinks.find(
            r => r.parentSubmissionId === s._id,
          );

          if (submissionLink) {
            let relatedSubmission = filteredSubmissions.find(
              s => s._id === submissionLink?.childSubmissionId,
            );

            if (relatedSubmission) {
              let meta = JSON.parse(relatedSubmission.metadataJSON);
              title = meta.Title || template.name;
            }
          }
        } else if (
          category.toUpperCase() === CATEGORY.EQUIPMENT &&
          s.metadataJSON &&
          s.metadataJSON !== ''
        ) {
          let meta = JSON.parse(s.metadataJSON);
          let vehicleId = meta[METADATA_KEYS.EQUIPMENTID];

          if (vehicleId && vehicleId !== '') {
            let equipment:
              | EquipmentVehicleProfile
              | EquipmentHeavyProfile
              | EquipmentOtherProfile
              | null = vehicles.find(v => v._id === vehicleId) || null;

            if (equipment === null) {
              equipment =
                heavyEquipments.find(v => v._id === vehicleId) || null;
            }

            if (equipment === null) {
              equipment =
                otherEquipments.find(v => v._id === vehicleId) || null;
            }

            if (equipment !== null) {
              subtitle = equipment.name;
            }
          }
        } else if (
          category.toUpperCase() === CATEGORY.OPERATIONS &&
          s.metadataJSON &&
          s.metadataJSON !== ''
        ) {
          let meta = JSON.parse(s.metadataJSON);
          title = 'Project/Site';
          tagline = template.name;

          if (
            template.name.trim().toLowerCase() ===
            COMMON_TEMPLATES.TASK.toLowerCase()
          ) {
            tagline = template.templateType.name;
          } else if (
            template.name.trim().toLowerCase() ===
              COMMON_TEMPLATES.MEETING_MINUTES.toLowerCase() &&
            meta[METADATA_KEYS.SUPERVISOR]
          ) {
            let supervisor = JSON.parse(meta[METADATA_KEYS.SUPERVISOR]);

            subtitle = supervisor.label;
          } else if (
            template.name.trim().toLowerCase() ===
              COMMON_TEMPLATES.SITE_INSPECTION.toLowerCase() &&
            meta[METADATA_KEYS.COMPLETED_BY]
          ) {
            let completedBy = JSON.parse(meta[METADATA_KEYS.COMPLETED_BY]);

            subtitle = completedBy.label;
          } else if (
            template.name.trim().toLowerCase() ===
              COMMON_TEMPLATES.FIRST_AID_ASSESSMENT.toLowerCase() &&
            meta[METADATA_KEYS.EMPLOYER]
          ) {
            subtitle = meta[METADATA_KEYS.EMPLOYER];
          }

          if (meta[METADATA_KEYS.PROJECT_NAME]) {
            title = meta[METADATA_KEYS.PROJECT_NAME];
          }
        } else if (
          category.toUpperCase() === CATEGORY.QUICKADD &&
          s.metadataJSON &&
          s.metadataJSON !== ''
        ) {
          let meta = JSON.parse(s.metadataJSON);
          tagline = category + ' ' + template.name;
          title = 'Project/Site';
          if (meta[METADATA_KEYS.PROJECT_NAME]) {
            title = meta[METADATA_KEYS.PROJECT_NAME];
          }

          if (meta.PersonInvolved) {
            let person = JSON.parse(meta.PersonInvolved);

            subtitle = person.label;
          }
        } else if (
          category.toUpperCase() === CATEGORY.REPORTS &&
          s.metadataJSON &&
          s.metadataJSON !== ''
        ) {
          let meta = JSON.parse(s.metadataJSON);
          tagline = template.name;
          title = 'Project/Site';
          if (meta[METADATA_KEYS.PROJECT_NAME]) {
            subtitle = meta[METADATA_KEYS.PROJECT_NAME];
          }
        }

        mappedRecords.push(
          getRecordFromSubmission(
            s._id,
            title,
            category,
            tagline,
            'submission',
            s.createDateTimeStamp,
            template.iconSvg ?? Icons.TASK,
            s.partition,
            s.metadataJSON,
            '',
            subtitle,
            s.displayId,
            s.templateType?.name,
          ),
        );
      }
    });

    // Fetch Submission Trainings
    let filteredTrainings = submissionTrainings.filter(
      s =>
        s.partition === orgId &&
        s.submissionStatus &&
        s.person &&
        s.submissionStatus.name === SubmissionStatuses.SUBMITTED,
    );
    filteredTrainings.forEach(function (t, i) {
      let category =
        t.trainingType && t.trainingType.name ? 'Training' : 'Certificate';
      let title = '';
      if (category === 'Training' && t.trainingType)
        title = t.trainingType!.name ?? '';
      else if (t.certificateType) title = t.certificateType!.name ?? '';

      mappedRecords.push(
        getRecordFromSubmission(
          t._id,
          title,
          category,
          category,
          'submissionTraining',
          t.createDateTimeStamp,
          category === 'Training' ? Icons.TRAINING : Icons.CERTIFICATION,
          t.partition,
          '',
          t.person!.id,
          t.person!.firstName + ' ' + t.person!.lastName,
          '',
          t.templateType?.name,
        ),
      );
    });

    let recordCategories = Array.from(
      new Set(mappedRecords.map(r => r.category).filter(c => c !== 'Home')),
    ).sort();
    setCategories(
      recordCategories
        .filter(x => x != TemplateCategoryTypes.AUDIT)
        .map(c => ({label: c, value: c})),
    );

    mappedRecords.sort((a, b) => b.datetime.getTime() - a.datetime.getTime());
    setRecords(mappedRecords);
  }

  async function fetchValueRecords(): Promise<void> {
    const mappedValueRecords: Record[] = [];

    let filteredTemplates = templates;
    let filteredVersions = templateVersions;

    let bundle = submissionBundles.find(b => b._id && b._id == props.bundleId);
    if (bundle && bundle.submissions) {
      for (let i = 0; i < bundle.submissions.length; i++) {
        let s = bundle.submissions[i];
        let displayId = '';
        let version = filteredVersions.find(
          v => v._id == s.templateVersion?._id,
        );
        let template = filteredTemplates.find(
          t => t._id == version?.templateId,
        );

        if (template) {
          let title = template.name;
          if (s.templateType?.name === 'Training Breakdown') {
            let training = submissionTrainings.find(
              t => s.submissionId && t._id == s.submissionId,
            );

            if (training && training.trainingType) {
              let category = training.trainingType.name
                ? 'Training'
                : 'Certificate';
              title =
                (category === 'Training'
                  ? training.trainingType!.name
                  : training.certificateType!.name) +
                ' - ' +
                training.person!.firstName +
                ' ' +
                training.person!.lastName;
            }
          } else {
            let sub = submissions.find(
              t => s.submissionId && t._id == s.submissionId,
            );

            if (sub && sub.displayId) {
              displayId = sub.displayId;
            }
          }
          mappedValueRecords.push(
            getRecordFromSubmission(
              s.submissionId ?? '',
              title,
              template.templateCategory.templateCategoryName,
              '',
              s.templateType?.name === 'Training Breakdown'
                ? 'submissionTraining'
                : 'submission',
              s.createDateTimeStamp!,
              template.iconSvg ?? Icons.TASK,
              bundle!.partition ?? '',
              s.metadataJSON ?? '',
              '',
              '',
              displayId,
              s.templateType?.name,
            ),
          );
        }
      }
    }

    mappedValueRecords.sort(
      (a, b) => a.datetime.getTime() - b.datetime.getTime(),
    );
    setValueRecords(mappedValueRecords);
  }

  function getRecordFromSubmission(
    id: string,
    title: string,
    category: string,
    tagline: string,
    type: 'submission' | 'submissionTraining',
    timeStamp: Date,
    icon: string,
    org: string,
    metadata: string,
    person?: string,
    personName?: string,
    displayId?: string | null,
    templateType?: string | null,
  ): Record {
    let date = moment(timeStamp).toDate();
    let project = '';
    if (metadata) {
      let meta = JSON.parse(metadata);
      if (meta[METADATA_KEYS.TITLE]) title = meta[METADATA_KEYS.TITLE];

      let metaDataKeyDate = '';
      let metaDataKeyTime = '';
      if (templateType === TemplateTypes.TASK) {
        metaDataKeyDate = METADATA_KEYS.DUEDATE;
        metaDataKeyTime = METADATA_KEYS.DUETIME;
      } else if (templateType === TemplateTypes.INCIDENT) {
        metaDataKeyDate = METADATA_KEYS.INCIDENTDATE;
        metaDataKeyTime = METADATA_KEYS.INCIDENTTIME;
      }

      let datePart = '';
      if (metaDataKeyDate) datePart = meta[metaDataKeyDate];

      let timePart = '';
      if (metaDataKeyTime) timePart = meta[metaDataKeyTime];

      if (datePart && timePart) {
        let time = moment(timePart, Formats.BACKEND_TIME);

        date = moment(datePart, Formats.BACKEND_DATE)
          .add(time.hours(), 'hours')
          .add(time.minutes(), 'minutes')
          .toDate();
      }

      if (meta[METADATA_KEYS.PROJECT_NAME])
        project = meta[METADATA_KEYS.PROJECT_NAME];

      if (person === '') {
        if (meta[METADATA_KEYS.WORKERID]) {
          try {
            let personObj = JSON.parse(
              meta[METADATA_KEYS.WORKERID],
            ) as MongoIdSqlId;

            person = personObj.mongoId ?? '';
            personName = personObj.label;
          } catch (error) {
            console.log('Error: Bad metadata serialization');
          }
        } else if (meta[METADATA_KEYS.ASSIGNED_TO]) {
          try {
            let personObj = JSON.parse(
              meta[METADATA_KEYS.ASSIGNED_TO],
            ) as MongoIdSqlId;

            person = personObj.mongoId ?? '';
            personName = personObj.label;
          } catch (error) {
            console.log('Error: Bad metadata serialization');
          }
        }

        if (meta[METADATA_KEYS.LEAD_INVESTIGATOR]) {
          let investigator = JSON.parse(meta[METADATA_KEYS.LEAD_INVESTIGATOR]);
          personName = investigator.label;
        }
      }
    }

    tagline = tagline === '' ? category : tagline;

    if (displayId) {
      tagline = displayId + ' - ' + tagline;
    }

    return {
      id: id,
      name: title,
      category: category,
      tagline: tagline,
      type: type,
      datetime: date,
      icon: icon,
      org: org,
      project: project,
      person: person,
      subTitle: personName,
    };
  }

  function showModal(): void {
    if (props.disabled) return;

    Keyboard.dismiss();

    setItemsToShow(defaultItemsToShow);
    setSearchFilter('');
    setIsShowingFilters(false);
    setFilteredCategory('');
    setFilteredProject('');
    setFilteredPerson('');
    setIsShowingModal(true);
  }

  function hideModal(): void {
    setIsShowingModal(false);
  }

  function clearSearchFilter(): void {
    Keyboard.dismiss();
    setSearchFilter('');
  }

  function showFilters(): void {
    if (isShowingFilters) {
      setIsShowingFilters(false);
    } else {
      setIsShowingFilters(true);
    }
  }

  function select(record: Record): void {
    let newSelection = [...selection];

    let index = newSelection.indexOf(record.id);
    if (index === -1) newSelection.push(record.id);
    else newSelection.splice(index, 1);

    setSelection(newSelection);
  }

  async function goToRecord(record: Record): Promise<void> {
    let externalView = record.org !== user.getUserInfo().orgId.toString();

    props.navigation.push(Navigation.BUNDLEFORM, {
      bundleId: props.bundleId,
      submissionId: record.id,
      submissionType: record.type,
      externalView: externalView,
    });
  }

  const showPDFModal = (record: Record) => {
    setRecordToPDF(record);
    setIsShowingPDFModal(true);
  };

  const deleteRecord = async (record?: Record) => {
    if (record) {
      let bundle = submissionBundles.find(
        b => b._id && b._id === props.bundleId,
      );
      if (bundle && bundle.submissions) {
        let newSubmissions = [
          ...bundle.submissions.filter(x => x.submissionId != record.id!),
        ];

        bundle.submissions = newSubmissions;
        updateSubmissionBundle(bundle);

        let newValue = [...value];
        let index = newValue.indexOf(record.id);
        if (index === -1) newValue.push(record.id);
        else newValue.splice(index, 1);
        setValue(newValue);
      }

      if (record.type === 'submission') {
        let submission = submissions.find(x => x._id == record!.id);

        if (submission) {
          submission.submissionStatusLock = LOCK_STATUS.LOCK;
          updateSubmission(submission);
        }
      } else {
        let training = submissionTrainings.find(x => x._id == record!.id);

        if (training) {
          training.submissionStatusLock = LOCK_STATUS.LOCK;
          updateSubmissionTraining(training);
        }
      }
    }
  };

  async function save(): Promise<void> {
    let currentUser = user.getUserInfo();
    let org = '';
    let currentEmail = '';
    if (currentUser) {
      org = currentUser.orgId.toString();
      currentEmail = currentUser.email;
    }

    let selectedSubmissions = submissions.filter(
      s => s.partition === org && selection.includes(s._id),
    );
    let selectedTrainings = submissionTrainings.filter(
      s => s.partition === org && selection.includes(s._id),
    );

    let bundle = submissionBundles.find(b => b._id && b._id === props.bundleId);
    if (bundle) {
      let bundleSubmissions: SubmissionBundle_submissions[] = [];

      //Loop through new selection to add reports to bundle and super lock them
      for (let i = 0; i < selection.length; i++) {
        let record = records.find(r => r.id === selection[i]);
        if (record) {
          if (record.type === 'submission') {
            let submission = selectedSubmissions.find(s => s._id == record!.id);

            if (submission) {
              bundleSubmissions.push({
                submissionId: submission._id,
                submissionSQLServerId: submission.SQLServerId,
                answers: submission.answers,
                metadataJSON: submission.metadataJSON,
                templateType: submission.templateType,
                templateVersion: submission.templateVersion,
                updatedBy: currentEmail as string,
                updateDateTimeStamp: new Date(),
              } as SubmissionBundle_submissions);

              submission.submissionStatusLock = LOCK_STATUS.SUPERLOCK;
              updateSubmission(submission);
            }
          } else {
            let training = selectedTrainings.find(s => s._id == record!.id);

            if (training) {
              bundleSubmissions.push({
                submissionId: training._id,
                answers: training.answers,
                metadataJSON: training.metadataJSON,
                templateType: {
                  _id: training.templateType?._id,
                  name: training.templateType?.name,
                },
                templateVersion: training.templateVersion,
                updatedBy: currentEmail as string,
                updateDateTimeStamp: new Date(),
                createDateTimeStamp: training.createDateTimeStamp,
                createdBy: training.createdBy,
                submissionSQLServerId: training.SQLServerId,
              } as SubmissionBundle_submissions);

              training.submissionStatusLock = LOCK_STATUS.SUPERLOCK;
              updateSubmissionTraining(training);
            }
          }
        }
      }

      //Loop through previous selected values not in the new selection
      //Remove the superlock to just lock if necessary
      for (let i = 0; i < value.length; i++) {
        let record = records.find(r => r.id === value[i]);
        let isSelected = selection.includes(value[i]);

        if (!isSelected && record) {
          let isInOtherBundle = submissionBundles.some(
            x =>
              x._id != bundle!._id &&
              x.submissions &&
              x.submissions.some(x => x.submissionId == record!.id),
          );

          if (!isInOtherBundle) {
            if (record.type === 'submission') {
              let submission = submissions.find(x => x._id == record!.id);

              if (submission) {
                submission.submissionStatusLock = LOCK_STATUS.LOCK;
                updateSubmission(submission);
              }
            } else {
              let training = submissionTrainings.find(x => x._id == record!.id);

              if (training) {
                training.submissionStatusLock = LOCK_STATUS.LOCK;
                updateSubmissionTraining(training);
              }
            }
          }
        }
      }

      bundle.submissions = bundleSubmissions;
      updateSubmissionBundle(bundle);
    }

    setValue(selection);
    setIsShowingModal(false);
  }

  function valid(): boolean {
    let isValid = true;
    let error = '';

    if (props.config?.required && value.length === 0) {
      isValid = false;
      error = (props.label ?? 'Documents') + ' are required';
    }

    if (props.showError) setError(error);

    return isValid;
  }

  const renderModal = (): React.ReactElement => {
    let availableRecords = 0;
    let visibleRecords: Record[] = [];

    if (searchFilter || filteredCategory || filteredProject || filteredPerson) {
      let project = '';
      try {
        if (filteredProject) {
          let projectObj = JSON.parse(filteredProject) as MongoIdSqlId;
          project = projectObj.label ?? '';
        }
      } catch (error) {
        console.log('Error: Bad control serialization');
      }

      let person = '';
      try {
        if (filteredPerson) {
          let personObj = JSON.parse(filteredPerson) as MongoIdSqlId;
          person = personObj.mongoId ?? '';
        }
      } catch (error) {
        console.log('Error: Bad control serialization');
      }

      let filteredRecords = records.filter(
        r =>
          (r.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
            r.tagline.toLowerCase().includes(searchFilter.toLowerCase())) &&
          (filteredCategory === '' || r.category == filteredCategory) &&
          (project === '' || r.project == project) &&
          (person === '' || r.person?.toLowerCase() == person?.toLowerCase()),
      );

      availableRecords = filteredRecords.length;
      visibleRecords = filteredRecords.slice(0, itemsToShow);
    } else {
      availableRecords = records.length;

      selection.forEach(val => {
        let item = records.find(r => r.id === val);
        if (item) visibleRecords.push(item);
      });

      visibleRecords = visibleRecords.filter(
        x => x.category !== TemplateCategoryTypes.AUDIT,
      );

      visibleRecords.sort(
        (a, b) => b.datetime.getTime() - a.datetime.getTime(),
      );

      //Complete the list with the top # elements
      for (
        let i = 0;
        i < records.length && visibleRecords.length < itemsToShow;
        i++
      ) {
        if (!visibleRecords.some(r => r.id === records[i].id))
          visibleRecords.push(records[i]);
      }
    }

    return (
      <Modal
        visible={isShowingModal && myKey === navKey}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                {props.label ?? 'FIRS Content'}
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={hideModal}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            {renderModalFilters()}
            <ScrollView style={{marginVertical: 20}}>
              {visibleRecords.map((record, index) => {
                return renderRecord(record, index);
              })}
              {visibleRecords.length < availableRecords && (
                <Pressable
                  style={({pressed}) => [
                    CommonStyleSheet.smallGreenButton,
                    {alignSelf: 'center', marginVertical: 12},
                    pressed && {
                      opacity: 0.8,
                    },
                  ]}
                  onPress={() =>
                    setItemsToShow(itemsToShow + defaultItemsToShow)
                  }>
                  <Text style={CommonStyleSheet.smallGreenButtonText}>
                    Show More
                  </Text>
                </Pressable>
              )}
            </ScrollView>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.greenButton,
                {alignSelf: 'center', marginTop: 0, marginBottom: 20},
                pressed && {
                  opacity: 0.8,
                },
              ]}
              onPress={save}>
              <Text style={CommonStyleSheet.greenButtonText}>Ok</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    );
  };

  const renderModalFilters = (): React.ReactElement => {
    let filtersApplied = false;

    if (searchFilter || filteredCategory || filteredProject || filteredPerson) {
      filtersApplied = true;
    }

    return (
      <View>
        <View
          style={[
            CommonStyleSheet.searchContainer,
            {paddingHorizontal: 0, marginTop: 12},
          ]}>
          <View style={CommonStyleSheet.searchBoxContainer}>
            <View style={CommonStyleSheet.searchBoxIcon}>
              <Icon icon={'input-search'} color={Colors.darkGreen} size={24} />
            </View>
            <TextInput
              returnKeyType='search'
              style={CommonStyleSheet.searchBoxInput}
              placeholder='Search'
              placeholderTextColor={Colors.gray}
              value={searchFilter}
              onChangeText={newText => setSearchFilter(newText)}
            />
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.searchBoxClear,
                {display: searchFilter === '' ? 'none' : 'flex'},
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 14,
                },
              ]}
              onPress={clearSearchFilter}>
              <Icon icon={'input-clear'} color={Colors.darkGreen} size={18} />
            </Pressable>
          </View>
          <View style={[CommonStyleSheet.searchFilters]}>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.searchFiltersContent,
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 40,
                },
                isShowingFilters && {
                  backgroundColor: Colors.darkGreen,
                  borderRadius: 40,
                },
                filtersApplied && {
                  backgroundColor: Colors.yellow,
                  borderRadius: 40,
                },
              ]}
              onPress={showFilters}>
              <Text
                style={
                  isShowingFilters || filtersApplied
                    ? CommonStyleSheet.searchFiltersTextLight
                    : CommonStyleSheet.searchFiltersText
                }>
                Filters
              </Text>
              <Icon
                icon={'input-filters'}
                color={
                  isShowingFilters || filtersApplied
                    ? Colors.white
                    : Colors.green
                }
                size={18}
              />
            </Pressable>
          </View>
        </View>
        {isShowingFilters && (
          <View
            style={{
              borderWidth: 1,
              borderColor: Colors.green,
              borderRadius: 12,
              padding: 12,
              marginTop: 12,
            }}>
            <Text
              style={{
                color: Colors.darkGreen,
                fontFamily: 'Poppins',
                fontSize: 14,
                fontWeight: '700',
                alignSelf: 'center',
              }}>
              FILTERS
            </Text>
            <DynamicSelect
              label='Document Category'
              config={{options: categories}}
              value={filteredCategory}
              onChange={(controlId, controlTypeId, value) => {
                setFilteredCategory(value);
              }}
            />
            <DynamicAutocomplete
              label='Project'
              config={{optionSource: ListDataTypes.PROJECTS}}
              value={filteredProject ? filteredProject : ''}
              onChange={(controlId, controlTypeId, value) =>
                setFilteredProject(value)
              }
            />
            <DynamicAutocomplete
              label='Person Involved'
              config={{optionSource: ListDataTypes.PEOPLE}}
              value={filteredPerson ? filteredPerson : ''}
              onChange={(controlId, controlTypeId, value) =>
                setFilteredPerson(value)
              }
            />
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.smallGreenButton,
                {alignSelf: 'center', marginVertical: 12},
                pressed && {
                  opacity: 0.8,
                },
              ]}
              onPress={() => {
                setFilteredCategory('');
                setFilteredProject('');
                setFilteredPerson('');
                setIsShowingFilters(false);
              }}>
              <Text style={CommonStyleSheet.smallGreenButtonText}>
                Clear Filters
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  };

  const renderRecord = (record: Record, index: number): React.ReactElement => {
    let checked = selection.some(s => s === record.id);
    return (
      <Pressable
        key={index}
        style={({pressed}) => [
          ReportsStyleSheet.listItemContainer,
          {padding: 0, marginBottom: 10},
          pressed && {
            backgroundColor: Colors.darkGreenTransparent,
          },
        ]}
        onPress={() => select(record)}>
        <img
          style={{maxWidth: '24px', maxHeight: '24px'}}
          src={`data:image/svg+xml;utf8,${encodeURIComponent(helperService.getTemplateIcon(record.icon, Colors.darkestGreen, Colors.white))}`}
        />
        <View style={[ReportsStyleSheet.listItemName, {paddingLeft: 12}]}>
          <Text style={[ReportsStyleSheet.listItemDate]} numberOfLines={1}>
            {record.tagline}
          </Text>
          <Text style={ReportsStyleSheet.listItemName} numberOfLines={1}>
            {record.name}
          </Text>
          {!!record.subTitle && record.subTitle !== '' && (
            <Text style={ReportsStyleSheet.listItemSubtitle} numberOfLines={1}>
              {record.subTitle}
            </Text>
          )}
        </View>
        <View style={ReportsStyleSheet.listItemDateContent}>
          <Text style={ReportsStyleSheet.listItemDate}>
            {moment(record.datetime).toDate().toLocaleDateString('en-us', {
              day: '2-digit',
              month: 'short',
            })}
          </Text>
          <Text style={ReportsStyleSheet.listItemTime}>
            {moment(record.datetime).toDate().toLocaleTimeString('en-us', {
              hourCycle: 'h24',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </View>
        <View
          style={[
            ControlsStyleSheet.checkBox,
            {marginHorizontal: 12},
            checked && {
              backgroundColor: Colors.teal,
              borderColor: Colors.teal,
            },
          ]}>
          <Icon icon={'checked'} color={Colors.white} size={16} />
        </View>
      </Pressable>
    );
  };

  const renderValueRecords = (): React.ReactElement => {
    let selectedRecords = valueRecords.filter(
      s => s.category != TemplateCategoryTypes.AUDIT && value.includes(s.id),
    );

    return (
      <>
        {selectedRecords.map((record, index) => {
          return renderValueRecord(record, index);
        })}
      </>
    );
  };

  const renderValueRecord = (
    record: Record,
    index: number,
  ): React.ReactElement => {
    return (
      <View
        key={index}
        style={[
          ControlsStyleSheet.attachmentsItem,
          {maxWidth: 320, paddingTop: 6},
        ]}>
        <img
          src={`data:image/svg+xml;utf8,${encodeURIComponent(helperService.getTemplateIcon(record.icon, Colors.green, Colors.white))}`}
        />
        <Text
          style={[
            ControlsStyleSheet.attachmentsText,
            {fontSize: 9, marginTop: 2},
          ]}
          numberOfLines={1}>
          {record.tagline}asd
        </Text>
        <Text
          style={[ControlsStyleSheet.attachmentsText, {marginTop: 2}]}
          numberOfLines={1}>
          {record.name}
        </Text>
        <Text
          style={[
            ControlsStyleSheet.attachmentsText,
            {fontSize: 9, marginTop: 2},
          ]}>
          {moment(record.datetime).toDate().toLocaleDateString('en-us', {
            day: '2-digit',
            month: 'short',
          }) + ' '}
          {moment(record.datetime).toDate().toLocaleTimeString('en-us', {
            hourCycle: 'h24',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
        <View style={ControlsStyleSheet.attachmentsActions}>
          <Pressable
            style={({pressed}) => [
              ControlsStyleSheet.attachmentsAction,
              pressed && {
                backgroundColor: Colors.lightTeal,
              },
            ]}
            onPress={() => goToRecord(record)}>
            <Icon icon={'eye'} color={Colors.green} size={20} />
          </Pressable>
          <Pressable
            style={({pressed}) => [
              ControlsStyleSheet.attachmentsAction,
              pressed && {
                backgroundColor: Colors.lightTeal,
              },
            ]}
            onPress={() => showPDFModal(record)}>
            <Icon icon={'print'} color={Colors.green} size={20} />
          </Pressable>
          {!props.disabled && (
            <Pressable
              style={({pressed}) => [
                ControlsStyleSheet.attachmentsAction,
                pressed && {
                  backgroundColor: Colors.lightTeal,
                },
              ]}
              onPress={() => deleteRecord(record)}>
              <Icon icon={'trash'} color={Colors.green} size={20} />
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  const renderPDFModal = (): React.ReactElement => {
    return (
      <Modal
        visible={isShowingPDFModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                Download Documents
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setIsShowingPDFModal(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              style={{marginVertical: 24}}>
              <View style={ControlsStyleSheet.attachmentsContainer}>
                {isShowingPDFModal && recordToPDF && (
                  <DynamicPDF
                    submissionId={recordToPDF.id}
                    type={recordToPDF.type}></DynamicPDF>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <>
      {renderValueRecords()}
      {renderModal()}
      {renderPDFModal()}
      <Text
        style={[ControlsStyleSheet.error, {display: error ? 'flex' : 'none'}]}>
        {error}
      </Text>
    </>
  );
};

export default DynamicBundleLinkRecord;
